import { useState } from 'react'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Divider from '@mui/material/Divider'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import {
  red, pink, purple, deepPurple, indigo, blue, lightBlue, cyan, teal,
  green, lightGreen, lime, yellow, amber, orange, deepOrange, brown, blueGrey,
} from '@mui/material/colors'
import { useTranslation } from 'react-i18next'
import { useOwnerAuth } from './OwnerAuthContext'
import { useShopSettings, useUpdateShop } from '../shared/api/shop'
import { isHttpUrl, isPhone } from '../shared/validation'
import type { ShopSettings, UpdateShopPayload } from '../shared/types'

const HEX = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/
const CURRENCY = /^[A-Z]{3}$/

// Brand-color palette picker (the substance of the MUI docs "Playground" — which is a docs-only
// demo, not an exported component): the Material Design hues at a few brand-friendly shades.
// @mui/material/colors is already installed, so this is a grid + click, no new dependency.
// ponytail: fixed shade set, no shade slider — the hex field / native picker cover exact values.
const HUES: Record<string, string>[] = [
  red, pink, purple, deepPurple, indigo, blue, lightBlue, cyan, teal,
  green, lightGreen, lime, yellow, amber, orange, deepOrange, brown, blueGrey,
]
const SHADES = ['300', '500', '700', '900'] as const

/**
 * OWNER-only shop settings (§16 Phase 7): branding, contact, currency and the stock-tracking
 * toggle. Saves to `/api/shop`; the public storefront reflects branding on its next load (its
 * `useShop` refetches on staleness/focus — no cross-session push needed for branding).
 */
export default function SettingsPage() {
  const { t } = useTranslation()
  const { isAuthenticated } = useOwnerAuth()
  const { data, isLoading, isError } = useShopSettings(isAuthenticated)

  return (
    <Container maxWidth="sm" sx={{ py: 3, pb: 8 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        {t('owner.settings.title')}
      </Typography>
      {isLoading ? (
        <CircularProgress />
      ) : isError || !data ? (
        <Alert severity="error">{t('owner.settings.loadError')}</Alert>
      ) : (
        // Keyed on slug so the form seeds its state once from the loaded settings.
        <SettingsForm key={data.slug} initial={data} />
      )}
    </Container>
  )
}

function SettingsForm({ initial }: { initial: ShopSettings }) {
  const { t } = useTranslation()
  const update = useUpdateShop()
  const [name, setName] = useState(initial.name)
  const [currency, setCurrency] = useState(initial.currency)
  const [brandColor, setBrandColor] = useState(initial.brandColor)
  const [logoUrl, setLogoUrl] = useState(initial.logoUrl ?? '')
  const [bannerUrl, setBannerUrl] = useState(initial.bannerUrl ?? '')
  const [tagline, setTagline] = useState(initial.tagline ?? '')
  const [description, setDescription] = useState(initial.description ?? '')
  const [phone, setPhone] = useState(initial.phone ?? '')
  const [address, setAddress] = useState(initial.address ?? '')
  const [stockTracking, setStockTracking] = useState(initial.stockTrackingEnabled)
  const [showPrices, setShowPrices] = useState(initial.showPrices)
  const [touched, setTouched] = useState(false)
  const [saved, setSaved] = useState(false)

  const nameError = !name.trim()
  const currencyError = !CURRENCY.test(currency)
  const colorError = !HEX.test(brandColor)
  const phoneError = !isPhone(phone) // optional field, but reject letters when present
  // Shown as <img> on every storefront page, so a web link or nothing (D-115).
  const logoError = !isHttpUrl(logoUrl)
  const bannerError = !isHttpUrl(bannerUrl)
  const invalid = nameError || currencyError || colorError || phoneError || logoError || bannerError

  const onSave = () => {
    setTouched(true)
    setSaved(false)
    if (invalid) {
      return
    }
    const payload: UpdateShopPayload = {
      name: name.trim(),
      currency: currency.trim().toUpperCase(),
      brandColor: brandColor.trim(),
      logoUrl: logoUrl.trim(),
      bannerUrl: bannerUrl.trim(),
      tagline: tagline.trim(),
      description: description.trim(),
      phone: phone.trim(),
      address: address.trim(),
      stockTrackingEnabled: stockTracking,
      showPrices,
    }
    // Reload after a save so the console re-tints: the theme reads brand_color from the public
    // useShop() cache, which a save doesn't touch — a full refresh re-fetches it (user request).
    // Brief beat first so the "Saved" confirmation is visible before the page reloads.
    update.mutate(payload, {
      onSuccess: () => {
        setSaved(true)
        setTimeout(() => window.location.reload(), 700)
      },
    })
  }

  return (
    <Stack spacing={2.5}>
      <Typography variant="subtitle2" color="text.secondary">
        {t('owner.settings.brandingSection')}
      </Typography>
      <TextField
        label={t('owner.settings.name')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={touched && nameError}
        helperText={touched && nameError ? t('common.required') : ' '}
        required
        fullWidth
        inputProps={{ maxLength: 255 }}
      />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <TextField
          label={t('owner.settings.brandColor')}
          value={brandColor}
          onChange={(e) => setBrandColor(e.target.value)}
          error={touched && colorError}
          helperText={touched && colorError ? t('owner.settings.colorHint') : ' '}
          sx={{ flexGrow: 1 }}
          inputProps={{ maxLength: 9 }}
        />
        {/* Native OS color picker (MUI Core ships no picker component) — doubles as the
            preview swatch. It only speaks 6-digit #rrggbb, so an 8-digit (alpha) value is
            trimmed for the input and an invalid one falls back to the default brand. Typing
            in the hex field above still drives it; picking here writes back a 6-digit hex. */}
        <Box
          component="input"
          type="color"
          aria-label={t('owner.settings.brandColor')}
          value={/^#[0-9a-fA-F]{6}/.test(brandColor) ? brandColor.slice(0, 7) : '#B06B3F'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBrandColor(e.target.value)}
          sx={{
            width: 44,
            height: 44,
            mt: 1,
            p: 0,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'transparent',
            cursor: 'pointer',
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {HUES.map((hue, i) => (
          <Stack key={i} spacing={0.5}>
            {SHADES.map((s) => {
              const c = hue[s]
              const selected = brandColor.toLowerCase() === c.toLowerCase()
              return (
                <Box
                  key={s}
                  component="button"
                  type="button"
                  aria-label={c}
                  onClick={() => setBrandColor(c)}
                  sx={{
                    width: 22,
                    height: 22,
                    p: 0,
                    border: 'none',
                    borderRadius: 0.5,
                    cursor: 'pointer',
                    bgcolor: c,
                    outline: selected ? '2px solid' : 'none',
                    outlineColor: 'text.primary',
                    outlineOffset: 1,
                  }}
                />
              )
            })}
          </Stack>
        ))}
      </Box>
      <TextField
        label={t('owner.settings.logoUrl')}
        value={logoUrl}
        onChange={(e) => setLogoUrl(e.target.value)}
        error={touched && logoError}
        helperText={touched && logoError ? t('validation.url') : ' '}
        fullWidth
        inputProps={{ maxLength: 1024 }}
      />
      <TextField
        label={t('owner.settings.bannerUrl')}
        value={bannerUrl}
        onChange={(e) => setBannerUrl(e.target.value)}
        error={touched && bannerError}
        helperText={touched && bannerError ? t('validation.url') : ' '}
        fullWidth
        inputProps={{ maxLength: 1024 }}
      />
      <TextField
        label={t('owner.settings.tagline')}
        value={tagline}
        onChange={(e) => setTagline(e.target.value)}
        fullWidth
        inputProps={{ maxLength: 255 }}
      />
      <TextField
        label={t('owner.settings.description')}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        fullWidth
        multiline
        minRows={2}
        inputProps={{ maxLength: 2000 }}
      />

      <Divider />
      <Typography variant="subtitle2" color="text.secondary">
        {t('owner.settings.contactSection')}
      </Typography>
      <TextField
        label={t('owner.settings.phone')}
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        fullWidth
        type="tel"
        error={touched && phoneError}
        helperText={touched && phoneError ? t('validation.phone') : ' '}
        inputProps={{ maxLength: 32 }}
      />
      <TextField
        label={t('owner.settings.address')}
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        fullWidth
        multiline
        minRows={2}
        inputProps={{ maxLength: 512 }}
      />

      <Divider />
      <Typography variant="subtitle2" color="text.secondary">
        {t('owner.settings.shopSection')}
      </Typography>
      <TextField
        label={t('owner.settings.currency')}
        value={currency}
        onChange={(e) => setCurrency(e.target.value.toUpperCase())}
        error={touched && currencyError}
        helperText={touched && currencyError ? t('owner.settings.currencyHint') : ' '}
        sx={{ width: 160 }}
        inputProps={{ maxLength: 3 }}
      />
      <Box>
        <FormControlLabel
          control={<Switch checked={stockTracking} onChange={(e) => setStockTracking(e.target.checked)} />}
          label={t('owner.settings.stockTracking')}
        />
        <Typography variant="body2" color="text.secondary">
          {t('owner.settings.stockTrackingHint')}
        </Typography>
      </Box>
      <Box>
        <FormControlLabel
          control={<Switch checked={showPrices} onChange={(e) => setShowPrices(e.target.checked)} />}
          label={t('owner.settings.showPrices')}
        />
        <Typography variant="body2" color="text.secondary">
          {t('owner.settings.showPricesHint')}
        </Typography>
      </Box>

      {update.isError && <Alert severity="error">{t('owner.settings.saveError')}</Alert>}
      {saved && !update.isPending && <Alert severity="success">{t('owner.settings.saved')}</Alert>}
      <Button variant="contained" size="large" onClick={onSave} disabled={update.isPending}>
        {update.isPending ? t('common.saving') : t('common.save')}
      </Button>
    </Stack>
  )
}
